import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, SCOPES } from '@/lib/permissions';
/**
 * React Query infinite hook to fetch flights.
 * @param {Object} params
 * @param {Object} [params.dateRange]
 * @param {Date} [params.dateRange.startDate]
 * @param {Date} [params.dateRange.endDate]
 * @param {string} [params.status]
 * @param {Object} [options]
 * @param {boolean} [options.enabled]
 * @param {number} [options.limit]
 * @returns {Object}
 */
export function useFlights(params = {}, options = {}) {

  const { userProfile } = useAuthStore();

  const profileId = userProfile?.userprofile_id;
  const orgId = userProfile?.org_id;

  const { dateRange, status } = params;
  const { enabled, limit = 10, ...queryOptions } = options;

  // Per-role data scope from the permission matrix.
  // self → quote_created_by: profileId (broker sees own flights)
  // global → broker_org_id: orgId (admin/coordinator/etc. see whole org)
  const { scope: getScope } = usePermissions();
  const trackingScope = getScope(MODULES.TRACKING);
  const scopeFilter =
    trackingScope === SCOPES.GLOBAL && orgId
      ? { broker_org_id: orgId }
      : trackingScope === SCOPES.SELF && profileId
      ? { quote_created_by: profileId }
      : null;

  const isEnabled = (enabled !== undefined ? enabled : true) && !!scopeFilter;
  const startKey = dateRange?.startDate
    ? new Date(dateRange.startDate).toISOString()
    : null;
  const endKey = dateRange?.endDate
    ? new Date(dateRange.endDate).toISOString()
    : null;

  return useInfiniteQuery({
    queryKey: ['flights', status ?? null, startKey, endKey, limit, trackingScope, profileId, orgId],
    queryFn: async ({ pageParam }) => {
      const skip = typeof pageParam === 'number' ? pageParam : 0;

      // Add 1-day buffer to account for UTC ↔ airport-local timezone differences.
      // Client-side filtering in TrackView trims results to the exact calendar range.
      const startDate = dateRange?.startDate ? new Date(dateRange.startDate) : null;
      const endDate = dateRange?.endDate ? new Date(dateRange.endDate) : null;
      if (startDate) {
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
      }
      if (endDate) {
        endDate.setDate(endDate.getDate() + 1);
        endDate.setHours(23, 59, 59, 999);
      }

      const filters = {
        ...(status ? { flight_status: status } : {}),
        ...(startDate ? { departure_date_from: startDate.toISOString() } : {}),
        ...(endDate ? { departure_date_to: endDate.toISOString() } : {}),
        ...scopeFilter,
        include: 'quotes,quotes.client,aircrafts,aircrafts.operator',
        skip,
        limit,
      };

      const flights = await apiClient.getFlights(filters);
      return Array.isArray(flights) ? flights : [];
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const lastCount = Array.isArray(lastPage) ? lastPage.length : 0;
      if (lastCount < limit) return undefined;
      return allPages.length * limit;
    },
    enabled: isEnabled,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
    ...queryOptions,
  });
}
